import { Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { useHistory } from 'react-router-dom';

import { ReactComponent as ConsumerReportsIcon } from '../../assets/images/consumer-reports.svg';
import { ReactComponent as DocusignIcon } from '../../assets/images/docusign.svg';
import { ReactComponent as DeelIcon } from '../../assets/images/icon-deel.svg';
import { ReactComponent as GoogleIcon } from '../../assets/images/icon-google-color.svg';
import { ReactComponent as JaneStreetIcon } from '../../assets/images/icon-jane-street.svg';
import { ReactComponent as KeysightIcon } from '../../assets/images/icon-keysight.svg';
import { ReactComponent as OutlookIcon } from '../../assets/images/icon-outlook.svg';
import { ReactComponent as PaceIcon } from '../../assets/images/icon-pace.svg';
import { ReactComponent as VercelIcon } from '../../assets/images/icon-vercel.svg';
import { ReactComponent as ImprintEngineIcon } from '../../assets/images/ie_square_logo.svg';
import {
  ERROR_LINK_CREDENTIALS_IS_NEEDED,
  EXISTING_ACCOUNT_MESSAGE,
  LOGIN_CONTENT_MAX_WIDTH,
  LOGIN_FORM_INITIAL_VALUES as initialValues,
  LOGIN_FORM_VALIDATION_SCHEMA as validationSchema,
  MAGIC_LINK_EMAIL_STORAGE_KEY,
  SupportedAuthProvidersEnum,
} from '../../constants/auth';
import { routes } from '../../constants/routing';
import * as AuthActions from '../../store/actions/auth';
import {
  selectIsConsumerReportsTenant,
  selectIsDeelTenant,
  selectIsDocusignTenant,
  selectIsImprintEngineTenant,
  selectIsJaneStreetTenant,
  selectIsKeysightTenant,
  selectIsPaceTenant,
  selectIsVercelTenant,
} from '../../store/selectors/shell';
import { IAuthenticateRequestPayload, IAuthError, ILoginFormState } from '../../types/auth';
import { EMAIL_INPUT } from '../../types/forms';
import { handleApiError } from '../../utils/ui';
import LoginPageLayout from './LoginPageLayout';

const LoginContainer = () => {
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const history = useHistory();

  const [linkFlowMethods, setLinkFlowMethods] = useState<SupportedAuthProvidersEnum[]>();

  const pendingCredsRef = useRef<IAuthError>();

  const isJaneStreet = useSelector(selectIsJaneStreetTenant);
  const isPace = useSelector(selectIsPaceTenant);
  const isVercel = useSelector(selectIsVercelTenant);
  const isDeel = useSelector(selectIsDeelTenant);
  const isConsumerReports = useSelector(selectIsConsumerReportsTenant);
  const isKeysight = useSelector(selectIsKeysightTenant);
  const isDocusign = useSelector(selectIsDocusignTenant);
  const isImprintEngine = useSelector(selectIsImprintEngineTenant);

  const startAuth = useCallback(
    async ({ providerId, email }: Omit<IAuthenticateRequestPayload, 'resolve' | 'reject'>) => {
      try {
        return await new Promise((resolve, reject) => {
          dispatch(
            AuthActions.authenticateRequest({
              providerId,
              email,
              pendingCredentials: pendingCredsRef.current,
              resolve,
              reject,
            }),
          );
        });
      } catch (error: any) {
        if (error.code === ERROR_LINK_CREDENTIALS_IS_NEEDED) {
          if (error?.email && error?.credential) {
            const { email: userEmail } = error;

            pendingCredsRef.current = error;

            const methods = await firebase.auth().fetchSignInMethodsForEmail(userEmail);

            setLinkFlowMethods(methods as SupportedAuthProvidersEnum[]);
          } else {
            handleApiError('Not authorized.')(error);
          }
        } else {
          handleApiError('Not authorized.')(error);
        }
      }
    },
    [dispatch, firebase],
  );

  const sendMagicLink = useCallback(
    (email: string) =>
      new Promise<void>((resolve, reject) => {
        dispatch(AuthActions.sendMagicLinkRequest({ email, resolve, reject }));
      }),
    [dispatch],
  );

  const onSubmit = useCallback(
    async (formValues: ILoginFormState, { setSubmitting }: FormikHelpers<ILoginFormState>) => {
      setSubmitting(true);
      try {
        await sendMagicLink(formValues.email);
        localStorage.setItem(MAGIC_LINK_EMAIL_STORAGE_KEY, formValues.email);
        history.push(routes.checkEmail);
      } catch (error) {
        handleApiError('Could not send sign-in link.')(error as any);
      } finally {
        setSubmitting(false);
      }
    },
    [sendMagicLink, history],
  );

  const getButtonIcon = useCallback((provider: SupportedAuthProvidersEnum) => {
    switch (provider) {
      case SupportedAuthProvidersEnum.Google:
        return <GoogleIcon />;
      case SupportedAuthProvidersEnum.Microsoft:
        return <OutlookIcon />;
      case SupportedAuthProvidersEnum.Pace:
        return <PaceIcon />;
      case SupportedAuthProvidersEnum.Vercel:
        return <VercelIcon />;
      case SupportedAuthProvidersEnum.JaneStreet:
        return <JaneStreetIcon />;
      case SupportedAuthProvidersEnum.Deel:
        return <DeelIcon />;
      case SupportedAuthProvidersEnum.ConsumerReports:
        return <ConsumerReportsIcon />;
      case SupportedAuthProvidersEnum.Keysight:
        return <KeysightIcon />;
      case SupportedAuthProvidersEnum.Docusign:
        return <DocusignIcon />;
      case SupportedAuthProvidersEnum.ImprintEngine:
        return <ImprintEngineIcon />;
      default:
        return null;
    }
  }, []);

  const getButtonTitle = useCallback((provider: SupportedAuthProvidersEnum) => {
    switch (provider) {
      case SupportedAuthProvidersEnum.Google:
        return 'Google';
      case SupportedAuthProvidersEnum.Microsoft:
        return 'Outlook';
      case SupportedAuthProvidersEnum.Pace:
        return 'Pace';
      case SupportedAuthProvidersEnum.Vercel:
        return 'Vercel';
      case SupportedAuthProvidersEnum.JaneStreet:
        return 'Jane Street';
      case SupportedAuthProvidersEnum.Deel:
        return 'Deel';
      case SupportedAuthProvidersEnum.ConsumerReports:
        return 'Okta';
      case SupportedAuthProvidersEnum.Keysight:
        return 'Keysight';
      case SupportedAuthProvidersEnum.ImprintEngine:
        return 'Okta';
      case SupportedAuthProvidersEnum.Docusign:
        return 'Docusign';
      default:
        return '';
    }
  }, []);

  const { handleSubmit, handleChange, handleBlur, values, touched, errors, isValid } = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const isLinkMode = useMemo(() => !!linkFlowMethods, [linkFlowMethods]);

  const shouldShowInLinkMode = useCallback(
    (providerId?: SupportedAuthProvidersEnum) => {
      if (!isLinkMode) {
        return true;
      }

      return providerId ? linkFlowMethods?.includes(providerId) : false;
    },
    [linkFlowMethods, isLinkMode],
  );

  const providerIds = useMemo(() => {
    const tenantGate: Partial<Record<SupportedAuthProvidersEnum, boolean>> = {
      [SupportedAuthProvidersEnum.JaneStreet]: isJaneStreet,
      [SupportedAuthProvidersEnum.Pace]: isPace,
      [SupportedAuthProvidersEnum.Vercel]: isVercel,
      [SupportedAuthProvidersEnum.Deel]: isDeel,
      [SupportedAuthProvidersEnum.ConsumerReports]: isConsumerReports,
      [SupportedAuthProvidersEnum.Keysight]: isKeysight,
      [SupportedAuthProvidersEnum.Docusign]: isDocusign,
      [SupportedAuthProvidersEnum.ImprintEngine]: isImprintEngine,
    };

    return Object.values(SupportedAuthProvidersEnum).filter((p) => shouldShowInLinkMode(p) && (tenantGate[p] ?? true));
  }, [
    shouldShowInLinkMode,
    isJaneStreet,
    isPace,
    isVercel,
    isDeel,
    isConsumerReports,
    isKeysight,
    isDocusign,
    isImprintEngine,
  ]);

  return (
    <LoginPageLayout>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ width: '100%', maxWidth: LOGIN_CONTENT_MAX_WIDTH }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
          {isLinkMode ? 'User already exists' : 'Sign in'}
        </Typography>

        {!isLinkMode && (
          <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 3 }}>
            Enter your email and we’ll send you a sign-in link
          </Typography>
        )}

        {isLinkMode && pendingCredsRef.current?.message && (
          <Typography align="center" sx={{ mx: 2.5, mb: 5 }}>
            {EXISTING_ACCOUNT_MESSAGE}
          </Typography>
        )}

        {shouldShowInLinkMode() && (
          <TextField
            fullWidth
            variant="outlined"
            label="Email"
            onChange={handleChange}
            id={EMAIL_INPUT}
            name={EMAIL_INPUT}
            type="email"
            onBlur={handleBlur}
            value={values.email}
            autoComplete="username"
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email ? errors.email : ''}
            sx={{
              mb: 3,
            }}
          />
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!isValid}
          fullWidth
          sx={{
            minHeight: 48,
            py: 1.5,
            px: 2.75,
          }}
        >
          Continue with email
        </Button>
      </Box>

      <Divider
        sx={{
          width: '100%',
          maxWidth: LOGIN_CONTENT_MAX_WIDTH,
          my: 3,
          '&::before, &::after': { borderColor: 'divider' },
          '& .MuiDivider-wrapper': { px: 2, color: 'text.secondary' },
        }}
      >
        or
      </Divider>

      <Stack sx={{ width: '100%', maxWidth: LOGIN_CONTENT_MAX_WIDTH }}>
        {providerIds.map((providerId) => (
          <Button
            key={providerId}
            variant="outlined"
            startIcon={getButtonIcon(providerId)}
            onClick={() => startAuth({ providerId })}
            sx={{
              position: 'relative',
              width: '100%',
              border: 1,
              borderColor: 'divider',
              color: 'text.primary',
              backgroundColor: 'background.paper',
              py: 1.5,
              px: 2,
              mb: 3,
              minHeight: 48,
              transition: (theme) => theme.transitions.create(['background-color', 'border-color']),
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'text.primary',
              },
              '& .MuiButton-startIcon': {
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                margin: 0,
              },
              '& svg': { width: 20, height: 20 },
            }}
          >
            Sign in with {getButtonTitle(providerId)}
          </Button>
        ))}
      </Stack>
    </LoginPageLayout>
  );
};

export default LoginContainer;
